import { useCallback, useEffect, useState } from "react";
import { useSelector } from "react-redux";
import { debounce } from "lodash";
import { useHistory } from "react-router-dom";

import { dispatch, getState } from "../../state/store";
import { selectAppsMarkets } from "../../state/selectors";
import {
  resetApps,
  resetSelectedApp,
  setActivePage,
  setAppsError,
  setAppsLoading,
  setAppsRefresh,
  setAppTypesGroups,
  setEditingLocale,
  setIsEmailMessageEditing,
  setIsFeaturesActiveSetted,
  setIsGeneralInfoSaved,
  setIsMarketsReassigned,
  setIsNeedToResetTextEditorState,
  setIsOriginalEmailSetted,
  setIsRolesReassigned,
  setLastActiveIndex,
  setManageType,
  setOriginalAvailableDashboards,
  setOriginalDefaultLocale,
  setOriginalEmailMessage,
  setSelectedApp,
  setUserAppTypes
} from "../../state/ducks";
import { setPrevLocale } from "../../state/ducks/apps";

import { appsService, appUserService, log, notificationService, t } from "../../services";
import {
  alignItems,
  appManageTypes,
  appsConfigurationOptions,
  appTypesNames,
  routePaths,
  strCode
} from "../../consts";
import { withSection } from "../../hoc";

import { FlexContainers, Heading5, PageContainer, WrapperContainer } from "../../components";
import { AppsTable, AppsTableControls, CreateFromAppModal } from "../../containers";

const AppsManagementPage = () => {
  const [searchValue, setSearchValue] = useState("");
  const [isCreateFromAppModalVisible, setIsCreateFromAppModalVisible] = useState(false);

  const history = useHistory();

  const apps = useSelector((store) => store.apps?.apps);
  const userAppTypes = useSelector((store) => store.configs.appTypes);

  const fetchApps = useCallback(async () => {
    dispatch(setAppsError(null));
    dispatch(setAppsLoading(true));

    try {
      dispatch(resetApps());

      const apps = await appsService.fetchApps();

      await appsService.fetchAppsMarkets(apps);

      if (appUserService.isAdmin()) {
        dispatch(setUserAppTypes([appTypesNames.allApps, ...apps.map((app) => app.appType)]));
        dispatch(setAppTypesGroups(apps));
      }
    } catch (err) {
      dispatch(setAppsError(err));

      log.error("AppsManagementPage: Failed to fetch apps", err);

      notificationService.showError({
        title: t(strCode.FETCH_APPS_ERROR),
        message: err?.response?.data?.message ? err.response.data.message : err?.toString()
      });
    } finally {
      dispatch(setAppsLoading(false));
      dispatch(setAppsRefresh(false));
    }
  }, []);

  useEffect(async () => {
    if (
      userAppTypes.length === 2 &&
      !appUserService.isAdmin() &&
      userAppTypes.some((type) => type === appTypesNames.allApps)
    ) {
      dispatch(setAppsLoading(true));

      const appType = userAppTypes.find((type) => type !== appTypesNames.allApps);

      const app = await appsService.fetchApp(appType);
      await appsService.fetchAppsMarkets([app]);

      const appsMarkets = selectAppsMarkets()(getState());

      dispatch(setManageType(appManageTypes.EDIT));
      dispatch(
        setSelectedApp({
          ...app,
          marketConfigs: appsMarkets[appType],
          groupConfigs: [],
          emailMessage: app.emailMessage || ""
        })
      );
      dispatch(setAppsLoading(false));

      history.push(routePaths.CREATE_APP);
    } else {
      // Reset app creation state. Put it here, because this state should be saved across consents pages
      // Debounce if used to handle some fields that also using debounce
      debounce(() => {
        dispatch(resetSelectedApp());
      }, 500);
      dispatch(setIsEmailMessageEditing(false));
      dispatch(setIsNeedToResetTextEditorState(true));
      dispatch(resetSelectedApp());
      dispatch(setActivePage(appsConfigurationOptions[0].id));
      dispatch(setLastActiveIndex(0));
      dispatch(setIsGeneralInfoSaved(false));
      dispatch(setManageType(null));
      dispatch(setOriginalEmailMessage(""));
      dispatch(setOriginalAvailableDashboards([]));
      dispatch(setEditingLocale(null));
      dispatch(setOriginalDefaultLocale(null));
      dispatch(setPrevLocale(null));
      dispatch(setIsFeaturesActiveSetted(false));
      dispatch(setIsMarketsReassigned(false));
      dispatch(setIsRolesReassigned(false));
      dispatch(setIsOriginalEmailSetted(false));

      fetchApps();
    }
  }, []);

  return (
    <PageContainer alignItems={alignItems.start}>
      <WrapperContainer
        Container={FlexContainers.FlexColumnStart}
        alignItems={alignItems.start}
        width="100%">
        <WrapperContainer
          Container={FlexContainers.FlexRowBetween}
          mb={24}
          width="100%"
          alignItems="start">
          <WrapperContainer Container={FlexContainers.FlexRowStart} gap={16}>
            <Heading5>{t(strCode.APPS_NAV_BTN)}</Heading5>
          </WrapperContainer>

          <AppsTableControls
            onCreateFromApp={() => setIsCreateFromAppModalVisible(true)}
            setSearchValue={setSearchValue}
            searchValue={searchValue}
          />
        </WrapperContainer>

        <AppsTable appsData={apps ? apps : []} searchValue={searchValue} />

        <CreateFromAppModal
          setActive={() => setIsCreateFromAppModalVisible(false)}
          isActive={isCreateFromAppModalVisible}
        />
      </WrapperContainer>
    </PageContainer>
  );
};

export default withSection(AppsManagementPage);
